import { useParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import PieChart from "./PieChart.jsx";
import proj4 from "proj4";



const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;


const REPORT_TABS = [
    { key: "growth_opportunity", label: "Growth & Opportunity" },
    { key: "land_use_urban_structure", label: "Land Use & Urban Structure" },
    { key: "environmental_constraints", label: "Environmental Constraints" },
    { key: "infrastructure_feasibility", label: "Infrastructure & Feasibility" },
    { key: "policy_approval_logic", label: "Policy & Approval Logic" },
];

function sortEntriesByDistance(obj) {
    if (!obj) return [];
    return Object.entries(obj).sort((a, b) => a[1].distance_km - b[1].distance_km);
}

// Area-weighted centroid of a GeoJSON Polygon / MultiPolygon (holes subtracted),
// matching the backend's shapely centroid. Returns { lat, lng } or null.
function polygonCentroid(geometry) {
    if (!geometry) return null;

    const polygons =
        geometry.type === "Polygon" ? [geometry.coordinates]
            : geometry.type === "MultiPolygon" ? geometry.coordinates
                : [];

    // Work relative to a local origin: with raw lng/lat (~ -79, 44) the shoelace terms
    // are ~3400 while a parcel's area is ~1e-6 deg², so precision is lost to cancellation.
    const origin = polygons[0]?.[0]?.[0];
    if (!origin) return null;
    const [ox, oy] = origin;

    let area = 0;
    let sumX = 0;
    let sumY = 0;

    polygons.forEach((rings) => {
        rings.forEach((ring, ringIndex) => {
            let cross2 = 0;
            let cx = 0;
            let cy = 0;

            for (let i = 0; i < ring.length; i++) {
                const next = ring[(i + 1) % ring.length];
                const x0 = ring[i][0] - ox;
                const y0 = ring[i][1] - oy;
                const x1 = next[0] - ox;
                const y1 = next[1] - oy;
                const cross = x0 * y1 - x1 * y0;
                cross2 += cross;
                cx += (x0 + x1) * cross;
                cy += (y0 + y1) * cross;
            }

            if (cross2 === 0) return;

            const ringArea = Math.abs(cross2) / 2;
            const sign = ringIndex === 0 ? 1 : -1; // first ring is the shell, the rest are holes
            area += sign * ringArea;
            sumX += sign * ringArea * (cx / (3 * cross2));
            sumY += sign * ringArea * (cy / (3 * cross2));
        });
    });

    if (area > 0) {
        return { lat: oy + sumY / area, lng: ox + sumX / area };
    }

    // degenerate (zero-area) geometry: fall back to the average of the shell's vertices
    const shell = polygons[0]?.[0];
    if (!shell?.length) return null;
    const lng = shell.reduce((s, [x]) => s + x, 0) / shell.length;
    const lat = shell.reduce((s, [, y]) => s + y, 0) / shell.length;
    return { lat, lng };
}

function InfoRow({ label, value }) {
    return (
        <div style={reportStyles.infoRow}>
            <div style={reportStyles.infoLabel}>{label}</div>
            <div style={reportStyles.infoValue}>{value ?? "-"}</div>
        </div>
    );
}

function SummaryCard({ title, children }) {
    return (
        <div style={reportStyles.summaryCard}>
            <h3 style={reportStyles.summaryCardTitle}>{title}</h3>
            {children}
        </div>
    );
}

function EmptyPanel({ title, text }) {
    return (
        <div style={reportStyles.emptyPanel}>
            <div style={reportStyles.emptyPanelTitle}>{title}</div>
            <div style={reportStyles.emptyPanelText}>{text}</div>
        </div>
    );
}

function StatusPill({ text, tone = "neutral" }) {
    const tones = {
        neutral: { background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" },
        good: { background: "#ecfdf5", color: "#166534", border: "1px solid #bbf7d0" },
        warn: { background: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa" },
        bad: { background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" },
    };

    return <span style={{ ...reportStyles.statusPill, ...(tones[tone] || tones.neutral) }}>{text}</span>;
}

function MetricCard({ label, value }) {
    return (
        <div style={reportStyles.metricCard}>
            <div style={reportStyles.metricLabel}>{label}</div>
            <div style={reportStyles.metricValue}>{value}</div>
        </div>
    );
}

function InfoChip({ label, value }) {
    return (
        <div style={reportStyles.infoChip}>
            <div style={reportStyles.infoChipLabel}>{label}</div>
            <div style={reportStyles.infoChipValue}>{value ?? "-"}</div>
        </div>
    );
}

function LayerStatusPill({ inside, within120m }) {
    if (inside) return <StatusPill text="Inside" tone="bad" />;
    if (within120m) return <StatusPill text="Near (120m)" tone="warn" />;
    return <StatusPill text="No Constraint" tone="good" />;
}

function ConstraintLayerCard({ title, tone = "neutral", children }) {
    const toneStyles = {
        neutral: { background: "#ffffff", border: "1px solid #e5e7eb" },
        green: { background: "#f0fdf4", border: "1px solid #bbf7d0" },
        woodland: { background: "#f8fafc", border: "1px solid #cbd5e1" },
    };

    return (
        <div style={{ ...reportStyles.constraintLayerCard, ...(toneStyles[tone] || toneStyles.neutral) }}>
            <h4 style={reportStyles.constraintLayerTitle}>{title}</h4>
            {children}
        </div>
    );
}

// 5-Layer Parcel Report layers that can be drawn on the map. The backend sends each one with
// closest_point + map_geometry; drawing works like the Layer Controls toggles.
const REPORT_MAP_LAYERS = {
    lu_centre: { label: "Nearest Regional Centre", color: "#C62828" },
    lu_mtsa: { label: "Nearest MTSA", color: "#AD1457" },
    lu_corridor: { label: "Nearest Regional Corridor", color: "#EF6C00" },
    env_greenlands: { label: "Regional Greenlands System", color: "#2E7D32" },
    env_wetland: { label: "Nearest wetland", color: "#00838F" },
    env_woodland: { label: "Nearest significant woodland", color: "#33691E" },
    env_ansi: { label: "Nearest ANSI", color: "#6A1B9A" },
    env_hydro: { label: "Nearest key hydrologic feature", color: "#1565C0" },
};

function ShowOnMapToggle({ checked, onChange, label = "Show on map" }) {
    return (
        <label style={reportStyles.showOnMapToggle}>
            <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} /> {label}
        </label>
    );
}

function isInsideMapTarget(target) {
    return !!target.inside || target.distance_m === 0 || target.distance_km === 0;
}

function extendBoundsWithGeometry(bounds, geom) {
    if (!geom) return;
    if (geom.type === "GeometryCollection") {
        geom.geometries.forEach((g) => extendBoundsWithGeometry(bounds, g));
        return;
    }
    const walk = (c) => (typeof c[0] === "number" ? bounds.extend({ lat: c[1], lng: c[0] }) : c.forEach(walk));
    walk(geom.coordinates);
}

// Draws one report layer (the feature, its closest point and a line from the project) and
// returns the created Google Maps objects so they can be removed later.
function drawReportMapTarget(map, target, style, origin) {
    const gm = window.google.maps;
    const objects = [];
    const toLatLng = ([lng, lat]) => ({ lat, lng });
    const toPath = (coords) => coords.map(toLatLng);
    const lineStyle = { strokeColor: style.color, strokeOpacity: 0.9, strokeWeight: 3 };
    const areaStyle = { strokeColor: style.color, strokeOpacity: 0.9, strokeWeight: 2, fillColor: style.color, fillOpacity: 0.2 };

    const addGeometry = (geom) => {
        if (!geom) return;
        switch (geom.type) {
            case "Point":
                objects.push(new gm.Marker({ map, position: toLatLng(geom.coordinates), title: style.label }));
                break;
            case "MultiPoint":
                geom.coordinates.forEach((c) => objects.push(new gm.Marker({ map, position: toLatLng(c), title: style.label })));
                break;
            case "LineString":
                objects.push(new gm.Polyline({ map, path: toPath(geom.coordinates), ...lineStyle }));
                break;
            case "MultiLineString":
                geom.coordinates.forEach((line) => objects.push(new gm.Polyline({ map, path: toPath(line), ...lineStyle })));
                break;
            case "Polygon":
                objects.push(new gm.Polygon({ map, paths: geom.coordinates.map(toPath), ...areaStyle }));
                break;
            case "MultiPolygon":
                geom.coordinates.forEach((rings) => objects.push(new gm.Polygon({ map, paths: rings.map(toPath), ...areaStyle })));
                break;
            case "GeometryCollection":
                geom.geometries.forEach(addGeometry);
                break;
            default:
                break;
        }
    };

    addGeometry(target.map_geometry);

    // closest point + line from the project (not needed when the project is inside the feature)
    if (!isInsideMapTarget(target)) {
        if (target.map_geometry?.type !== "Point") {
            objects.push(new gm.Marker({
                map,
                position: target.closest_point,
                title: `Closest point: ${style.label}`,
                icon: { path: gm.SymbolPath.CIRCLE, scale: 6, fillColor: style.color, fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
            }));
        }
        if (origin) {
            objects.push(new gm.Polyline({ map, path: [origin, target.closest_point], strokeColor: style.color, strokeOpacity: 0.9, strokeWeight: 2 }));
        }
    }

    return objects;
}

function ReportTabContent({ activeTab, parcelReport, loadingLandUse, loadingEnvironmental, renderMapToggle = () => null }) {
    if (!parcelReport) {
        return (
            <EmptyPanel
                title="No parcel selected"
                text="Click on a parcel to view its full report here."
            />
        );
    }

    if (activeTab === "land_use_urban_structure") {
        const lu = parcelReport.land_use_urban_structure || {};
        const employmentArea = lu.employment_area || {};
        const urbanStructure = lu.urban_structure || {};
        const ruralSettlement = lu.rural_settlement || {};
        const nearestCentre = lu.nearest_regional_centre || {};
        const nearestMtsa = lu.nearest_mtsa || {};
        const nearestCorridor = lu.nearest_regional_corridor || {};

        if (!lu.primary_designation && loadingLandUse) {
            return (
                <div style={reportStyles.reportTabContent}>
                    <EmptyPanel title="Loading land use data" text="Fetching Official Plan land use and urban structure layers for this parcel." />
                </div>
            );
        }

        return (
            <div style={reportStyles.reportTabContent}>
                <div style={reportStyles.reportHeroCard}>
                    <div style={reportStyles.reportHeroHeader}>
                        <div>
                            <div style={reportStyles.reportHeroEyebrow}>Layer Report</div>
                            <h3 style={reportStyles.reportHeroTitle}>Land Use & Urban Structure</h3>
                        </div>
                        <StatusPill
                            text={lu.primary_designation || "Unknown"}
                            tone={employmentArea.inside ? "warn" : lu.within_urban_boundary ? "good" : "neutral"}
                        />
                    </div>

                    <div style={reportStyles.metricGrid}>
                        <MetricCard label="Within Urban Boundary" value={lu.within_urban_boundary ? "Yes" : "No"} />
                        <MetricCard label="Municipality" value={lu.municipality || "-"} />
                        <MetricCard label="Application Type" value={lu.application_type || "-"} />
                    </div>

                    <div style={reportStyles.messageBox}>
                        {employmentArea.inside
                            ? `This parcel is located within the "${employmentArea.name}" Employment Area (${employmentArea.municipality}).`
                            : ruralSettlement.inside
                                ? `This parcel is located within the "${ruralSettlement.name}" ${ruralSettlement.type}.`
                                : lu.within_urban_boundary
                                    ? "This parcel is a Community Area within the Regional Urban Boundary."
                                    : "This parcel is outside the Regional Urban Boundary."}
                    </div>
                </div>

                <div style={reportStyles.layerGrid}>
                    <ConstraintLayerCard title="Land Use Designation">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Primary Designation" value={lu.primary_designation} />
                            <InfoChip label="Land Use (Application)" value={lu.land_use} />
                            <InfoChip label="Within Urban Boundary" value={lu.within_urban_boundary ? "Yes" : "No"} />
                            <InfoChip label="Agricultural System" value={lu.agricultural_system ? "Yes" : "No"} />
                        </div>
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Employment Area" tone={employmentArea.inside ? "neutral" : "woodland"}>
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside Employment Area" value={employmentArea.inside ? "Yes" : "No"} />
                            <InfoChip label="Name" value={employmentArea.name} />
                            <InfoChip label="Gross Area (ha)" value={employmentArea.gross_hectares ? Math.round(employmentArea.gross_hectares) : "-"} />
                            <InfoChip label="Cluster" value={employmentArea.cluster} />
                            <InfoChip
                                label="Density Target"
                                value={employmentArea.density_target_jobs_per_ha != null ? `${employmentArea.density_target_jobs_per_ha} jobs/ha` : "-"}
                            />
                        </div>
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Urban Structure" tone="green">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Designated Greenfield Area" value={urbanStructure.designated_greenfield_area ? "Yes" : "No"} />
                            <InfoChip label="New Community Area" value={urbanStructure.new_community_area ? "Yes" : "No"} />
                            <InfoChip label="Built-up Area" value={urbanStructure.built_up_area ? "Yes" : "No"} />
                        </div>
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Regional Structure Proximity">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip
                                label="Nearest Regional Centre"
                                value={nearestCentre.name ? `${nearestCentre.name} (${nearestCentre.distance_km} km)` : "-"}
                            />
                            <InfoChip
                                label="Nearest MTSA"
                                value={nearestMtsa.name ? `${nearestMtsa.name} (${nearestMtsa.distance_km} km)` : "-"}
                            />
                            <InfoChip label="MTSA Status" value={nearestMtsa.status} />
                            <InfoChip label="MTSA Stop Type" value={nearestMtsa.stop_type} />
                            <InfoChip
                                label="Nearest Regional Corridor"
                                value={nearestCorridor.name ? `${nearestCorridor.name} (${nearestCorridor.distance_km} km)` : "-"}
                            />
                        </div>
                        {renderMapToggle("lu_centre", "Show nearest Regional Centre on map")}
                        {renderMapToggle("lu_mtsa", "Show nearest MTSA on map")}
                        {renderMapToggle("lu_corridor", "Show nearest Regional Corridor on map")}
                    </ConstraintLayerCard>
                </div>
            </div>
        );
    }

    if (activeTab === "environmental_constraints") {
        const summary = parcelReport.constraint_summary || {};
        const env = parcelReport.environmental_constraints;

        if (!env && loadingEnvironmental) {
            return (
                <div style={reportStyles.reportTabContent}>
                    <EmptyPanel title="Loading environmental data" text="Fetching York Region Official Plan Chapter 3 (Natural Environment) layers for this parcel." />
                </div>
            );
        }

        const greenlands = env?.regional_greenlands || {};
        const wetland = env?.wetland || {};
        const woodland = env?.woodland || {};
        const ansi = env?.ansi || {};
        const orm = env?.oak_ridges_moraine || {};
        const greenbelt = env?.greenbelt || {};
        const water = env?.water_resource_system || {};
        const landform = env?.orm_landform_conservation || {};
        const hazard = env?.floodplain_hazard || {};
        const otherHydro = env?.nearest_other_hydrologic_feature || {};

        const levelTone = {
            MAJOR: "bad",
            HIGH: "bad",
            MODERATE: "warn",
            LOW: "warn",
            NONE: "good",
        };

        return (
            <div style={reportStyles.reportTabContent}>
                <div style={reportStyles.reportHeroCard}>
                    <div style={reportStyles.reportHeroHeader}>
                        <div>
                            <div style={reportStyles.reportHeroEyebrow}>Layer Report</div>
                            <h3 style={reportStyles.reportHeroTitle}>Environmental Constraints</h3>
                        </div>
                        <StatusPill
                            text={summary.status || "Unknown"}
                            tone={levelTone[summary.status] || "neutral"}
                        />
                    </div>

                    <div style={reportStyles.metricGrid}>
                        <MetricCard label="Hard Reject" value={summary.hard_reject ? "Yes" : "No"} />
                        <MetricCard label="EIS Likely Required" value={env?.environmental_impact_study_required ? "Yes" : "No"} />
                        <MetricCard label="Project Score" value={parcelReport.score_text} />
                    </div>

                    <div style={reportStyles.messageBox}>{summary.message || "No environmental summary returned."}</div>
                </div>

                <div style={reportStyles.layerGrid}>
                    <ConstraintLayerCard title="Regional Greenlands System" tone="green">
                        <div style={reportStyles.layerTopStatus}>
                            <LayerStatusPill inside={greenlands.inside} within120m={greenlands.within_120m} />
                        </div>
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside Greenlands" value={greenlands.inside ? "Yes" : "No"} />
                            <InfoChip label="Distance" value={greenlands.distance_m != null ? `${Math.round(greenlands.distance_m)} m` : "-"} />
                            <InfoChip label="Within 120m (EIS)" value={greenlands.within_120m ? "Yes" : "No"} />
                        </div>
                        <div style={reportStyles.layerDescription}>
                            {greenlands.inside
                                ? "Development and site alteration are prohibited within the Regional Greenlands System (policy 3.2.3)."
                                : greenlands.within_120m
                                    ? "Outside Greenlands, but within the 120m influence area — an Environmental Impact Study is required (policy 3.2.4)."
                                    : "Not currently affected by the Regional Greenlands System proximity threshold."}
                        </div>
                        {renderMapToggle("env_greenlands")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Wetland" tone={wetland.provincially_significant ? "neutral" : "woodland"}>
                        <div style={reportStyles.layerTopStatus}>
                            <LayerStatusPill inside={wetland.inside} within120m={wetland.within_120m} />
                        </div>
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside Wetland" value={wetland.inside ? "Yes" : "No"} />
                            <InfoChip label="Distance" value={wetland.distance_m != null ? `${Math.round(wetland.distance_m)} m` : "-"} />
                            <InfoChip label="Type" value={wetland.wetland_type} />
                            <InfoChip label="Significance" value={wetland.significance} />
                        </div>
                        <div style={reportStyles.layerDescription}>
                            {wetland.provincially_significant
                                ? "Provincially Significant / Provincial Plan Area wetland — development is prohibited here (policy 3.4.19)."
                                : wetland.within_120m
                                    ? "Within 120m of a wetland — an Environmental Impact Study is required (policy 3.4.20/3.4.21)."
                                    : "Not currently affected by the wetland proximity threshold."}
                        </div>
                        {renderMapToggle("env_wetland")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Significant Woodland" tone="woodland">
                        <div style={reportStyles.layerTopStatus}>
                            <LayerStatusPill inside={woodland.inside} within120m={woodland.within_120m} />
                        </div>
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside Woodland" value={woodland.inside ? "Yes" : "No"} />
                            <InfoChip label="Distance" value={woodland.distance_m != null ? `${Math.round(woodland.distance_m)} m` : "-"} />
                            <InfoChip label="Within 120m (EIS)" value={woodland.within_120m ? "Yes" : "No"} />
                        </div>
                        <div style={reportStyles.layerDescription}>
                            {woodland.inside
                                ? "Development and site alteration are prohibited within mapped woodland and its vegetation protection zone (policy 3.4.27)."
                                : woodland.within_120m
                                    ? "Within 120m of mapped woodland — an Environmental Impact Study is required."
                                    : "Not currently affected by the woodland proximity threshold."}
                        </div>
                        {renderMapToggle("env_woodland")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Area of Natural and Scientific Interest">
                        <div style={reportStyles.layerTopStatus}>
                            <LayerStatusPill inside={ansi.inside} within120m={ansi.within_120m} />
                        </div>
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside ANSI" value={ansi.inside ? "Yes" : "No"} />
                            <InfoChip label="Name" value={ansi.name} />
                            <InfoChip label="Class" value={ansi.class} />
                            <InfoChip label="Significance" value={ansi.significance} />
                            <InfoChip label="Distance" value={ansi.distance_m != null ? `${Math.round(ansi.distance_m)} m` : "-"} />
                        </div>
                        {renderMapToggle("env_ansi")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Oak Ridges Moraine / Greenbelt">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside ORM" value={orm.inside ? "Yes" : "No"} />
                            <InfoChip label="ORM Designation" value={orm.designation} />
                            <InfoChip label="Inside Greenbelt" value={greenbelt.inside ? "Yes" : "No"} />
                            <InfoChip label="Greenbelt Designation" value={greenbelt.designation} />
                            <InfoChip label="Growth Plan NHS" value={env?.growth_plan_natural_heritage_system ? "Yes" : "No"} />
                        </div>
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Water Resource System">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="High Vulnerable Aquifer" value={water.inside_high_vulnerable_aquifer ? "Yes" : "No"} />
                            <InfoChip label="Wellhead Protection Area" value={water.inside_wellhead_protection_area ? (water.wellhead_protection_area_name || "Yes") : "No"} />
                            <InfoChip label="Intake Protection Zone" value={water.inside_intake_protection_zone ? "Yes" : "No"} />
                            <InfoChip label="Groundwater Recharge Area" value={water.inside_groundwater_recharge_area ? "Yes" : "No"} />
                            <InfoChip label="Surface Water Contribution Area" value={water.inside_surface_water_contribution_area ? "Yes" : "No"} />
                        </div>
                        <div style={reportStyles.layerDescription}>
                            These are sensitivity flags under policy 3.3, not automatic rejects — they may trigger best-management-practice or Contaminant Management Plan requirements for specific uses.
                        </div>
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="ORM Landform Conservation">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside Landform Conservation Area" value={landform.inside ? "Yes" : "No"} />
                            <InfoChip label="Class" value={landform.class} />
                        </div>
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Other Key Hydrologic Features">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Nearest Feature" value={otherHydro.type} />
                            <InfoChip label="Distance" value={otherHydro.distance_m != null ? `${Math.round(otherHydro.distance_m)} m` : "-"} />
                        </div>
                        <div style={reportStyles.layerDescription}>
                            Covers lakes, watercourses, seepage areas/springs and kettle lakes (policy 3.4.1).
                        </div>
                        {renderMapToggle("env_hydro")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Floodplain / Hazard Lands" tone={hazard.inside ? "neutral" : "woodland"}>
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Inside Hazard Land" value={hazard.inside ? "Yes" : "No"} />
                            <InfoChip label="Criteria" value={hazard.criteria} />
                        </div>
                        <div style={reportStyles.layerDescription}>
                            Source: {hazard.data_source}. {hazard.coverage_note}
                        </div>
                    </ConstraintLayerCard>
                </div>
            </div>
        );
    }

    if (activeTab === "infrastructure_feasibility") {
        const infra = parcelReport.infrastructure_feasibility || {};
        const busStop = infra.nearest_bus_stop;
        const busStopDistance = busStop?.distance_km != null ? `${busStop.distance_km} km` : infra.no_bus_stop_within_10km ? "None within 10 km" : "-";
        const srt = infra.nearest_srt;
        const childrenService = infra.children_service;
        const nearbyStores = infra.nearby_stores;

        return (
            <div style={reportStyles.reportTabContent}>
                <div style={reportStyles.reportHeroCard}>
                    <div style={reportStyles.reportHeroEyebrow}>Layer Report</div>
                    <h3 style={reportStyles.reportHeroTitle}>Infrastructure & Feasibility</h3>

                    <div style={reportStyles.metricGrid}>
                        <MetricCard label="Nearest Bus Stop" value={busStopDistance} />
                        <MetricCard label="Nearest Transit Line" value={srt?.distance_km != null ? `${srt.distance_km} km` : "-"} />
                        <MetricCard label="Children Service" value={childrenService?.distance_km != null ? `${childrenService.distance_km} km` : "-"} />
                    </div>

                    <div style={reportStyles.messageBoxSoft}>
                        Transit, retail and children's-service proximity for this parcel.
                    </div>
                </div>

                <div style={reportStyles.layerGrid}>
                    <ConstraintLayerCard title="Nearest Bus Stop">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Name" value={busStop?.name} />
                            <InfoChip label="Distance" value={busStopDistance} />
                        </div>
                        {renderMapToggle("infra_bus")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Surface Rapid Transit Service">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Distance" value={srt?.distance_km != null ? `${srt.distance_km} km` : "-"} />
                        </div>
                        {renderMapToggle("infra_srt")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Nearest Groceries">
                        {sortEntriesByDistance(nearbyStores?.grocery).map(([brand, store]) => (
                            <InfoRow key={brand} label={brand} value={`${store.distance_km} km`} />
                        ))}
                        {renderMapToggle("infra_groceries")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Nearest Banks">
                        {sortEntriesByDistance(nearbyStores?.banks).map(([brand, bank]) => (
                            <InfoRow key={brand} label={brand} value={`${bank.distance_km} km`} />
                        ))}
                        {renderMapToggle("infra_banks")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Nearest Pharmacy">
                        {sortEntriesByDistance(nearbyStores?.pharmacy).map(([brand, p]) => (
                            <InfoRow key={brand} label={brand} value={`${p.distance_km} km`} />
                        ))}
                        {renderMapToggle("infra_pharmacy")}
                    </ConstraintLayerCard>

                    <ConstraintLayerCard title="Children Services">
                        <div style={reportStyles.infoGrid}>
                            <InfoChip label="Name" value={childrenService?.feature?.properties?.NAME} />
                            <InfoChip label="Category" value={childrenService?.feature?.properties?.CATEGORY} />
                            <InfoChip label="Distance" value={childrenService?.distance_km != null ? `${childrenService.distance_km} km` : "-"} />
                        </div>
                        {renderMapToggle("infra_children")}
                    </ConstraintLayerCard>
                </div>
            </div>
        );
    }

    const fallbackMap = {
        growth_opportunity: {
            title: "Growth & Opportunity",
            text: "Growth potential, corridor proximity and overall scoring are shown here.",
            raw: parcelReport.growth_opportunity,
        },
        policy_approval_logic: {
            title: "Policy & Approval Logic",
            text: "Policy alignment, approval risk and review logic can be connected here.",
            raw: parcelReport.policy_approval_logic,
        },
    };

    const item = fallbackMap[activeTab];

    return (
        <div style={reportStyles.reportTabContent}>
            <div style={reportStyles.reportHeroCard}>
                <div style={reportStyles.reportHeroEyebrow}>Layer Report</div>
                <h3 style={reportStyles.reportHeroTitle}>{item?.title || "No content"}</h3>
                <div style={reportStyles.messageBoxSoft}>{item?.text || "No data available."}</div>
            </div>

            {item?.raw ? (
                <div style={reportStyles.reportSection}>
                    <h4 style={reportStyles.reportSectionTitle}>Returned Data</h4>
                    <pre style={reportStyles.preBlock}>{JSON.stringify(item.raw, null, 2)}</pre>
                </div>
            ) : (
                <EmptyPanel title="Waiting for backend data" text="This layer UI is ready. Backend data can be connected here." />
            )}
        </div>
    );
}

// A parcel opened from the Parcels page ("Analyze Project"), mapped onto the project fields
// this page reads. Its shape and attributes come from Parcel.csv via the parcels-csv API.
function parcelToProject(parcel) {
    const a = parcel.attributes || {};
    return {
        PAR_GIS_ID: parcel.parcel_id,
        APPL_NAME: a.LOCATION || `Parcel ${parcel.parcel_id}`,
        ADDRESS: a.LOCATION || null,
        MUN_NAME: a.MUNNAME || null,
        PLANNUM: a.PLANNUM || null,
        PAR_TYPE: a.PAR_TYPE || null,
        LIFESTATUS: a.LIFESTATUS || null,
        Shape__Area: a.SHAPE_Area ? Number(a.SHAPE_Area) : null,
        geometry: parcel.geometry,
    };
}

// source: "application" (/projects/:id, York Region development applications) or
// "parcel" (/projects/parcel/:parcelId, from the Parcels page)
const ProjectDetail = ({ source = "application" }) => {
    const [activeTab, setActiveTab] = useState("official");
    const [reportActiveTab, setReportActiveTab] = useState("environmental_constraints");

    const API_BASE = process.env.REACT_APP_API_BASE || "";


    const facilityCardStyle = {
        marginTop: "6px",
        padding: "12px",
        background: "#f49d1a34",
        borderRadius: "10px",
        borderLeft: "4px solid #F49E1A",
        minWidth: "240px",
    };
    const [projectScore, setProjectScore] = useState(null);

    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const busMarkerRef = useRef(null);
    const busArcRef = useRef(null);


    const { id, parcelId } = useParams();
    const isParcelSource = source === "parcel";
    const location = useLocation();
    const stateCentroid = location.state?.centroid;

    const [showBusStopOnMap, setShowBusStopOnMap] = useState(false);


    // Score inputs (bus stop, corridor, SRT, greenland): undefined while loading, null when
    // nothing was found (e.g. no bus stop within 10 km) or the request failed.
    const [busStop, setBusStop] = useState(undefined);
    const [loadingBus, setLoadingBus] = useState(false);
    const [busError, setBusError] = useState(null);



    const [loadingCorridor, setLoadingCorridor] = useState(false);


    const [nearestCorridor, setNearestCorridor] = useState(undefined);
    const [showCorridorOnMap, setShowCorridorOnMap] = useState(false);

    const corridorPolygonRef = useRef(null);
    const corridorPointRef = useRef(null);
    const corridorLineRef = useRef(null);


    const corridorLayerRef = useRef(null);

    const [nearbyStores, setNearbyStores] = useState(null);
    const [loadingStores, setLoadingStores] = useState(false);

    const [showGroceriesOnMap, setShowGroceriesOnMap] = useState(false);
    const [showBanksOnMap, setShowBanksOnMap] = useState(false);
    const [showPharmacyOnMap, setShowPharmacyOnMap] = useState(false);

    const facilityMarkersRef = useRef([]);


    const [nearestSRT, setNearestSRT] = useState(undefined);
    const [showSRTOnMap, setShowSRTOnMap] = useState(false);

    const srtLineRef = useRef(null);
    const srtClosestPointRef = useRef(null);
    const srtLinkLineRef = useRef(null);

    const [childrenService, setChildrenService] = useState(null);
    const [showChildrenServiceOnMap, setShowChildrenServiceOnMap] = useState(false);

    const childrenMarkerRef = useRef(null);
    const childrenLineRef = useRef(null);


    const [nearestGreenland, setNearestGreenland] = useState(undefined);
    const [loadingGreenland, setLoadingGreenland] = useState(false);
    // set when one of the score inputs failed to load, so the score shows "Unavailable"
    const [scoreInputsFailed, setScoreInputsFailed] = useState(false);
    const [showGreenlandOnMap, setShowGreenlandOnMap] = useState(false);

    const [landUseReport, setLandUseReport] = useState(null);
    const [loadingLandUse, setLoadingLandUse] = useState(false);

    const [environmentalReport, setEnvironmentalReport] = useState(null);
    const [loadingEnvironmental, setLoadingEnvironmental] = useState(false);

    // 5-Layer Parcel Report "Show on map" toggles (land use + environmental layers)
    const [reportMapLayers, setReportMapLayers] = useState({});
    const reportMapObjectsRef = useRef({});
    const reportMapTargets = useMemo(() => ({
        lu_centre: landUseReport?.nearest_regional_centre,
        lu_mtsa: landUseReport?.nearest_mtsa,
        lu_corridor: landUseReport?.nearest_regional_corridor,
        env_greenlands: environmentalReport?.regional_greenlands,
        env_wetland: environmentalReport?.wetland,
        env_woodland: environmentalReport?.woodland,
        env_ansi: environmentalReport?.ansi,
        env_hydro: environmentalReport?.nearest_other_hydrologic_feature,
    }), [landUseReport, environmentalReport]);

    const greenlandPolygonRef = useRef(null);
    const greenlandPointRef = useRef(null);
    const greenlandLineRef = useRef(null);



    const [project, setProject] = useState(null);
    const [projectNotFound, setProjectNotFound] = useState(null); // { title, text } when it can't be shown
    const [zoningData, setZoningData] = useState(null); // ✅ اضافه شد

    // Router state only carries a centroid when arriving from the Dashboard or the map.
    // On a shared link, bookmark or new tab, derive it from the project's own geometry.
    // Memoized because `centroid` is a dependency of the analysis effects below.
    const geometryCentroid = useMemo(() => polygonCentroid(project?.geometry), [project]);
    const centroid = stateCentroid ?? geometryCentroid;

    const loaderStyle = {
        display: "inline-block",
        height: "1rem",
        width: "5rem",
        backgroundColor: "#ddd",
        borderRadius: "4px",
        animation: "pulse 1.5s infinite ease-in-out",
    };

    const spinnerStyle = {
        width: "20px",
        height: "20px",
        border: "3px solid #ccc",
        borderTop: "3px solid #666",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
    };

    const keyframes = `
@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

    proj4.defs(
        "EPSG:26917",
        "+proj=utm +zone=17 +datum=WGS84 +units=m +no_defs"
    );


    useEffect(() => {
        const style = document.createElement("style");
        style.innerHTML = keyframes;
        document.head.appendChild(style);
    }, []);

    //نشون دادن تزدیک ترین فاصله تا ایسنگاه اتوبوس
    useEffect(() => {
        if (!centroid) return;

        const fetchNearestBusStop = async () => {
            try {
                setLoadingBus(true);

                const res = await fetch(
                    `${API_BASE}/api/analysis/nearest-bus-stop/`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            lat: centroid.lat,
                            lng: centroid.lng,
                        }),
                    }
                );

                if (!res.ok) {
                    throw new Error("Failed to fetch bus stop");
                }

                const data = await res.json();
                setBusStop(data.nearest_bus_stop ?? null);
            } catch (err) {
                console.error(err);
                setBusError("Unable to load nearest bus stop");
                setBusStop(null);
                setScoreInputsFailed(true);
            } finally {
                setLoadingBus(false);
            }
        };

        fetchNearestBusStop();
    }, [centroid]);





    useEffect(() => {
        const fetchParcel = async () => {
            try {
                setProjectNotFound(null);

                if (!/^\d+$/.test(String(parcelId))) {
                    setProjectNotFound({ title: "Parcel not found", text: `"${parcelId}" is not a valid parcel ID.` });
                    return;
                }

                const res = await fetch(`${API_BASE}/api/analysis/parcels-csv/parcel/${parcelId}/`);
                const data = await res.json();

                if (res.status === 404) {
                    setProjectNotFound({ title: "Parcel not found", text: `No parcel with ID "${parcelId}" was found in Parcel.csv.` });
                    return;
                }
                if (!res.ok) {
                    throw new Error(data.error || `HTTP ${res.status}`);
                }
                if (!data.has_shape) {
                    setProjectNotFound({
                        title: "Parcel has no shape yet",
                        text: `Parcel ${parcelId} is only in Parcel.csv and has no shape on the parcel map, so it can't be analyzed yet.`,
                    });
                    return;
                }

                setProject(parcelToProject(data));
            } catch (err) {
                console.error("❌ Error loading parcel:", err);
                setProjectNotFound({ title: "Could not load the parcel", text: `${err.message || err}. Please refresh the page to try again.` });
            }
        };

        const fetchProjectFromArcGIS = async () => {
            try {
                setProjectNotFound(null);

                // 1) Query only this project by SFORCEID. The layer returns at most 2000
                // records per request, so downloading everything (where=1=1) misses most
                // applications. orderByFields keeps the pick stable if an ID is duplicated.
                const where = `SFORCEID='${String(id).trim().replace(/'/g, "''")}'`;
                const url =
                    "https://services1.arcgis.com/GzvOwaQBbX7KLiuG/arcgis/rest/services/Hosted_Development_Application_Status_And_Teams/FeatureServer/0/query" +
                    `?where=${encodeURIComponent(where)}&outFields=*&orderByFields=objectid&f=geojson`;

                const res = await fetch(url);
                const data = await res.json();

                if (!res.ok || data.error) {
                    throw new Error(data.error?.message || `ArcGIS returned ${res.status}`);
                }

                // 2) Take the matching feature
                const found = data.features?.[0];

                if (!found) {
                    console.error("❌ Project not found in ArcGIS dataset");
                    setProjectNotFound({
                        title: "Project not found",
                        text: `No development application with ID "${id}" was found in York Region's ArcGIS data.`,
                    });
                    return;
                }

                // 3) Clean project data for React
                setProject({
                    ...found.properties,
                    geometry: found.geometry
                });

            } catch (err) {
                console.error("❌ Error loading ArcGIS project:", err);
            }
        };

        if (isParcelSource) {
            fetchParcel();
        } else {
            fetchProjectFromArcGIS();
        }
    }, [id, parcelId, isParcelSource]);



    useEffect(() => {
        if (!project) return;

        const initMap = async () => {
            const center = centroid ?? { lat: 43.7, lng: -79.4 };

            await new Promise((resolve) => {
                if (window.google?.maps) return resolve();

                const script = document.createElement("script");
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}`;
                script.async = true;
                script.defer = true;
                script.onload = resolve;
                document.body.appendChild(script);
            });

            if (mapInstanceRef.current) return;

            const map = new window.google.maps.Map(
                document.getElementById("map"),
                {
                    center,
                    zoom: centroid ? 15 : 12,
                }
            );

            mapInstanceRef.current = map;

            // 🟥 Polygon پروژه (فقط یک‌بار)
            // (multi-part shapes are drawn part by part; some parcels and applications have them)
            const polygons =
                project.geometry?.type === "Polygon" ? [project.geometry.coordinates]
                    : project.geometry?.type === "MultiPolygon" ? project.geometry.coordinates
                        : [];

            if (polygons.length) {
                const bounds = new window.google.maps.LatLngBounds();

                polygons.forEach((rings) => {
                    rings[0].forEach(([lng, lat]) => {
                        bounds.extend({ lat, lng });
                    });

                    new window.google.maps.Polygon({
                        paths: rings[0].map(([lng, lat]) => ({ lat, lng })),
                        strokeColor: "#FF0000",
                        strokeOpacity: 0.9,
                        strokeWeight: 2,
                        fillColor: "#FF0000",
                        fillOpacity: 0.25,
                        map,
                    });
                });

                map.fitBounds(bounds);
            }
        };

        initMap();
    }, [project, centroid]);



    //on o off check box bus station
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        // ⛔ checkbox خاموش
        if (!showBusStopOnMap) {
            // حذف marker اتوبوس
            if (busMarkerRef.current) {
                busMarkerRef.current.setMap(null);
                busMarkerRef.current = null;
            }

            // حذف arc
            if (busArcRef.current) {
                busArcRef.current.setMap(null);
                busArcRef.current = null;
            }

            // 🎯 بازگشت زوم فقط به پروژه
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) => {
                    bounds.extend({ lat, lng });
                });
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }

            return;
        }

        // ✅ checkbox روشن
        if (!busStop?.lat || !busStop?.lng || !centroid) return;

        const busPos = {
            lat: Number(busStop.lat),
            lng: Number(busStop.lng),
        };

        // 🚌 Marker اتوبوس
        if (!busMarkerRef.current) {
            busMarkerRef.current = new window.google.maps.Marker({
                position: busPos,
                map,
                title: busStop.name || "Nearest Bus Stop",
                icon: {
                    url: "https://maps.gstatic.com/mapfiles/transit/iw2/6/bus.png",
                    scaledSize: new window.google.maps.Size(32, 32),
                },
            });
        }

        // 🌈 Arc بین پروژه و ایستگاه
        if (!busArcRef.current) {
            const arcPath = createArcPath(centroid, busPos);

            busArcRef.current = new window.google.maps.Polyline({
                path: arcPath,
                geodesic: true,
                strokeColor: "#F4B400", // 🟡 زرد
                strokeOpacity: 0.9,
                strokeWeight: 3,
                map,
            });
        }

        // 🎯 زوم فقط یک بار برای هر دو
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(centroid);
        bounds.extend(busPos);
        map.fitBounds(bounds);

    }, [showBusStopOnMap]);


    //nearest corridor  
    useEffect(() => {
        if (!centroid) return;

        const fetchNearestCorridor = async () => {
            try {
                const res = await fetch(`${API_BASE}/api/analysis/nearest-corridor/`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        lat: centroid.lat,
                        lng: centroid.lng,
                    }),
                });

                if (!res.ok) {
                    throw new Error(`nearest-corridor returned ${res.status}`);
                }

                const data = await res.json();
                setNearestCorridor(data ?? null);
            } catch (err) {
                console.error("❌ Failed to load corridor", err);
                setNearestCorridor(null);
                setScoreInputsFailed(true);
            }
        };

        fetchNearestCorridor();
    }, [centroid]);



    //on o off check boxRegional coridor , reional center
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        // ⛔ خاموش
        if (!showCorridorOnMap) {
            corridorPolygonRef.current?.setMap(null);
            corridorPointRef.current?.setMap(null);
            corridorLineRef.current?.setMap(null);

            corridorPolygonRef.current = null;
            corridorPointRef.current = null;
            corridorLineRef.current = null;

            // 🔁 زوم برگردد روی پروژه
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) =>
                    bounds.extend({ lat, lng })
                );
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }
            return;
        }

        // ✅ روشن
        if (!nearestCorridor?.geometry) return;

        // 🟡 Polygon
        const coords =
            nearestCorridor.geometry.type === "Polygon"
                ? nearestCorridor.geometry.coordinates[0]
                : nearestCorridor.geometry.coordinates[0][0];

        const path = coords.map(([lng, lat]) => ({ lat, lng }));

        corridorPolygonRef.current = new window.google.maps.Polygon({
            paths: path,
            strokeColor: "#FFA500",
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: "#FFD700",
            fillOpacity: 0.25,
            map,
        });

        // 📍 closest point
        const closest = nearestCorridor.closest_point;
        corridorPointRef.current = new window.google.maps.Marker({
            position: closest,
            map,
            title: "Closest point on corridor",
            icon: "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png",
        });

        // 📐 خط centroid → corridor
        if (centroid) {
            corridorLineRef.current = new window.google.maps.Polyline({
                path: [centroid, closest],
                strokeColor: "#FFA500",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                map,
            });
        }

        // 🎯 زوم
        const bounds = new window.google.maps.LatLngBounds();

        if (centroid) bounds.extend(centroid);
        bounds.extend(nearestCorridor.closest_point);

        map.fitBounds(bounds);


    }, [showCorridorOnMap, nearestCorridor]);



    // nearets srt
    useEffect(() => {
        if (!centroid) return;

        const fetchNearestSRT = async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/api/analysis/nearest-srt/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            lat: centroid.lat,
                            lng: centroid.lng,
                        }),
                    }
                );

                if (!res.ok) {
                    throw new Error(`nearest-srt returned ${res.status}`);
                }

                const data = await res.json();
                setNearestSRT(data ?? null);
            } catch (err) {
                console.error("❌ Failed to load SRT", err);
                setNearestSRT(null);
                setScoreInputsFailed(true);
            }
        };

        fetchNearestSRT();
    }, [centroid]);


    // Fetch nearest children service
    useEffect(() => {
        if (!centroid) return;

        const fetchNearestChildrenService = async () => {
            try {
                const res = await fetch(
                    `${API_BASE}/api/analysis/nearest-children-service/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            lat: centroid.lat,
                            lng: centroid.lng,
                        }),
                    }
                );

                if (!res.ok) {
                    throw new Error(`nearest-children-service returned ${res.status}`);
                }

                const data = await res.json();
                setChildrenService(data);
            } catch (err) {
                console.error("❌ Failed to load children service", err);
            }
        };

        fetchNearestChildrenService();
    }, [centroid]);


    // on off fetchNearestChildrenService

    useEffect(() => {
        if (!mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        // ⛔ خاموش
        if (!showChildrenServiceOnMap) {
            childrenMarkerRef.current?.setMap(null);
            childrenLineRef.current?.setMap(null);

            childrenMarkerRef.current = null;
            childrenLineRef.current = null;

            // برگشت به پروژه
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) =>
                    bounds.extend({ lat, lng })
                );
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }
            return;
        }

        // ✅ روشن
        if (!childrenService?.feature || !centroid) return;

        const [lng, lat] =
            childrenService.feature.geometry.coordinates;

        const pos = { lat, lng };

        // 📍 Marker
        if (!childrenMarkerRef.current) {
            childrenMarkerRef.current = new window.google.maps.Marker({
                position: pos,
                map,
                title: childrenService.feature.properties.NAME,
                icon: {
                    url: "https://maps.google.com/mapfiles/ms/icons/purple-dot.png",
                    scaledSize: new window.google.maps.Size(30, 30),
                },
            });
        }

        // 📐 خط ارتباط
        if (!childrenLineRef.current) {
            childrenLineRef.current = new window.google.maps.Polyline({
                path: [centroid, pos],
                strokeColor: "#7B1FA2",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                map,
            });
        }

        // 🎯 زوم دقیق
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(centroid);
        bounds.extend(pos);
        map.fitBounds(bounds);

    }, [showChildrenServiceOnMap, childrenService]);




    // on or off srt

    useEffect(() => {
        if (!mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        // ⛔ خاموش
        if (!showSRTOnMap) {
            srtLineRef.current?.setMap(null);
            srtClosestPointRef.current?.setMap(null);
            srtLinkLineRef.current?.setMap(null);

            srtLineRef.current = null;
            srtClosestPointRef.current = null;
            srtLinkLineRef.current = null;

            // برگشت زوم به پروژه
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) =>
                    bounds.extend({ lat, lng })
                );
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }
            return;
        }

        // ✅ روشن
        if (!nearestSRT?.geometry || !nearestSRT.closest_point) return;

        // 🔁 LineString → LatLng
        const path = nearestSRT.geometry.coordinates.map(([x, y]) => {
            const [lng, lat] = proj4("EPSG:26917", "EPSG:4326", [x, y]);
            return { lat, lng };
        });

        // 🟠 خط SRT
        srtLineRef.current = new window.google.maps.Polyline({
            path,
            strokeColor: "#FF6F00",
            strokeOpacity: 0.9,
            strokeWeight: 4,
            map,
        });

        // 📍 closest point
        srtClosestPointRef.current = new window.google.maps.Marker({
            position: nearestSRT.closest_point,
            map,
            title: "Closest SRT point",
            icon: "http://maps.google.com/mapfiles/ms/icons/orange-dot.png",
        });

        // 📐 خط پروژه → SRT
        if (centroid) {
            srtLinkLineRef.current = new window.google.maps.Polyline({
                path: [centroid, nearestSRT.closest_point],
                strokeColor: "#FF6F00",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                map,
            });
        }

        // 🎯 زوم فقط بین این دو
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(centroid);
        bounds.extend(nearestSRT.closest_point);
        map.fitBounds(bounds);

    }, [showSRTOnMap, nearestSRT]);





    // greenland

    useEffect(() => {
        if (!centroid) return;

        const fetchNearestGreenland = async () => {
            try {
                setLoadingGreenland(true);

                const res = await fetch(
                    `${API_BASE}/api/analysis/nearest-greenland/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            lat: centroid.lat,
                            lng: centroid.lng,
                        }),
                    }
                );

                if (!res.ok) {
                    throw new Error(`nearest-greenland returned ${res.status}`);
                }

                const data = await res.json();
                setNearestGreenland(data ?? null);
            } catch (err) {
                console.error("❌ Failed to load greenland", err);
                setNearestGreenland(null);
                setScoreInputsFailed(true);
            } finally {
                setLoadingGreenland(false);
            }
        };

        fetchNearestGreenland();
    }, [centroid]);

    // land use & urban structure

    useEffect(() => {
        if (!centroid) return;

        const fetchLandUseReport = async () => {
            try {
                setLoadingLandUse(true);

                const res = await fetch(
                    `${API_BASE}/api/analysis/land-use-report/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            lat: centroid.lat,
                            lng: centroid.lng,
                        }),
                    }
                );

                const data = await res.json();
                setLandUseReport(data);
            } catch (err) {
                console.error("❌ Failed to load land use report", err);
            } finally {
                setLoadingLandUse(false);
            }
        };

        fetchLandUseReport();
    }, [centroid]);

    // environmental constraints (Chapter 3)

    useEffect(() => {
        if (!centroid) return;

        const fetchEnvironmentalReport = async () => {
            try {
                setLoadingEnvironmental(true);

                const res = await fetch(
                    `${API_BASE}/api/analysis/environmental-constraints-report/`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            lat: centroid.lat,
                            lng: centroid.lng,
                        }),
                    }
                );

                const data = await res.json();
                setEnvironmentalReport(data);
            } catch (err) {
                console.error("❌ Failed to load environmental constraints report", err);
            } finally {
                setLoadingEnvironmental(false);
            }
        };

        fetchEnvironmentalReport();
    }, [centroid]);

    // on off greenland

    useEffect(() => {
        if (!mapInstanceRef.current) return;
        const map = mapInstanceRef.current;

        // ⛔ خاموش
        if (!showGreenlandOnMap) {
            greenlandPolygonRef.current?.setMap(null);
            greenlandPointRef.current?.setMap(null);
            greenlandLineRef.current?.setMap(null);

            greenlandPolygonRef.current = null;
            greenlandPointRef.current = null;
            greenlandLineRef.current = null;

            // 🔁 بازگشت زوم به پروژه
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) =>
                    bounds.extend({ lat, lng })
                );
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }
            return;
        }

        // ✅ روشن
        if (!nearestGreenland?.geometry) return;

        // 🟢 Polygon گرین‌لند
        const coords =
            nearestGreenland.geometry.type === "Polygon"
                ? nearestGreenland.geometry.coordinates[0]
                : nearestGreenland.geometry.coordinates[0][0];

        const path = coords.map(([lng, lat]) => ({ lat, lng }));

        greenlandPolygonRef.current = new window.google.maps.Polygon({
            paths: path,
            strokeColor: "#2E7D32",
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: "#66BB6A",
            fillOpacity: 0.25,
            map,
        });

        // 📍 closest point
        const closest = nearestGreenland.closest_point;
        greenlandPointRef.current = new window.google.maps.Marker({
            position: closest,
            map,
            title: "Closest point on GreenLand",
            icon: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
        });

        // 📐 خط فاصله
        if (centroid) {
            greenlandLineRef.current = new window.google.maps.Polyline({
                path: [centroid, closest],
                strokeColor: "#2E7D32",
                strokeOpacity: 0.9,
                strokeWeight: 2,
                map,
            });
        }

        // 🎯 زوم
        const bounds = new window.google.maps.LatLngBounds();
        if (centroid) bounds.extend(centroid);
        bounds.extend(closest);
        map.fitBounds(bounds);

    }, [showGreenlandOnMap, nearestGreenland]);

    // 5-Layer Parcel Report layers: draw/remove the ones whose checkbox changed
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map || !window.google?.maps) return;

        const drawn = reportMapObjectsRef.current;
        let shown = null;
        let hidden = false;

        Object.keys(REPORT_MAP_LAYERS).forEach((key) => {
            const target = reportMapTargets[key];
            const wanted = !!reportMapLayers[key] && !!target?.closest_point;

            if (!wanted && drawn[key]) {
                drawn[key].forEach((o) => o.setMap(null));
                delete drawn[key];
                hidden = true;
            } else if (wanted && !drawn[key]) {
                drawn[key] = drawReportMapTarget(map, target, REPORT_MAP_LAYERS[key], centroid);
                shown = target;
            }
        });

        if (shown) {
            // zoom to the project and the closest point (or to the feature when the project is inside it)
            const bounds = new window.google.maps.LatLngBounds();
            if (isInsideMapTarget(shown)) {
                extendBoundsWithGeometry(bounds, shown.map_geometry);
            } else {
                if (centroid) bounds.extend(centroid);
                bounds.extend(shown.closest_point);
            }
            map.fitBounds(bounds);
        } else if (hidden) {
            // zoom back to the project, like the other toggles
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) =>
                    bounds.extend({ lat, lng })
                );
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }
        }
    }, [reportMapLayers, reportMapTargets, centroid, project]);



    const createArcPath = (start, end, segments = 30, curvature = 0.1) => {
        const points = [];

        const lat1 = start.lat;
        const lng1 = start.lng;
        const lat2 = end.lat;
        const lng2 = end.lng;

        const dx = lng2 - lng1;
        const dy = lat2 - lat1;

        // نقطه وسط
        const mx = (lng1 + lng2) / 2;
        const my = (lat1 + lat2) / 2;

        // بردار عمود (perpendicular)
        const nx = -dy;
        const ny = dx;

        for (let i = 0; i <= segments; i++) {
            const t = i / segments;

            // Interpolation خطی
            const lat = lat1 + dy * t;
            const lng = lng1 + dx * t;

            // میزان انحنا (سینوسی → نرم)
            const offset = Math.sin(Math.PI * t) * curvature;

            points.push({
                lat: lat + ny * offset,
                lng: lng + nx * offset,
            });
        }

        return points;
    };

    const sortByDistance = (obj) => {
        if (!obj) return [];
        return Object.entries(obj).sort(
            (a, b) => a[1].distance_km - b[1].distance_km
        );
    };


    useEffect(() => {
        if (!centroid) return;

        const fetchNearbyStores = async () => {
            try {
                setLoadingStores(true);

                const res = await fetch(
                    `${API_BASE}/api/analysis/nearby-stores/?lat=${centroid.lat}&lng=${centroid.lng}`
                );

                const data = await res.json();
                setNearbyStores(data.results);

            } catch (err) {
                console.error("❌ Failed to load nearby stores", err);
            } finally {
                setLoadingStores(false);
            }
        };

        fetchNearbyStores();
    }, [centroid]);

    const extractPlaces = (category) => {
        if (!nearbyStores || !nearbyStores[category]) return [];
        return Object.values(nearbyStores[category]);
    };
    useEffect(() => {
        if (!mapInstanceRef.current) return;

        const map = mapInstanceRef.current;

        // پاک‌سازی قبلی
        facilityMarkersRef.current.forEach(m => m.setMap(null));
        facilityMarkersRef.current = [];

        let activePlaces = [];

        if (showGroceriesOnMap) activePlaces.push(...extractPlaces("grocery"));
        if (showBanksOnMap) activePlaces.push(...extractPlaces("banks"));
        if (showPharmacyOnMap) activePlaces.push(...extractPlaces("pharmacy"));

        // ⛔ اگر هیچ‌کدام فعال نیست
        if (activePlaces.length === 0) {
            if (project?.geometry?.type === "Polygon") {
                const bounds = new window.google.maps.LatLngBounds();
                project.geometry.coordinates[0].forEach(([lng, lat]) =>
                    bounds.extend({ lat, lng })
                );
                map.fitBounds(bounds);
            } else if (centroid) {
                map.setCenter(centroid);
                map.setZoom(15);
            }
            return;
        }

        // ✅ نمایش روی نقشه
        const bounds = new window.google.maps.LatLngBounds();
        if (centroid) bounds.extend(centroid);

        activePlaces.forEach(place => {
            const marker = new window.google.maps.Marker({
                position: { lat: place.lat, lng: place.lng },
                map,
                title: place.name,
                icon: {
                    url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
                    scaledSize: new window.google.maps.Size(28, 28),
                },
            });

            const info = new window.google.maps.InfoWindow({
                content: `
                <strong>${place.name}</strong><br/>
                ${place.address}<br/>
                Distance: ${place.distance_km} km
            `,
            });

            marker.addListener("click", () => info.open(map, marker));

            facilityMarkersRef.current.push(marker);
            bounds.extend(marker.getPosition());
        });

        map.fitBounds(bounds);

    }, [showGroceriesOnMap, showBanksOnMap, showPharmacyOnMap, nearbyStores]);




    // ================= SCORING FUNCTIONS =================

    // 1. Regional Corridor / Center (max 3.5)
    const scoreCorridor = (dist) => {
        if (dist == null) return 0;
        if (dist <= 0.5) return 3.5;
        if (dist <= 1) return 3.0;
        if (dist <= 2) return 2.0;
        if (dist <= 5) return 1.0;
        return 0;
    };

    // 2. Regional Greenlands (max 2.5)
    const scoreGreenland = (inside) => {
        return inside ? 0 : 2.5;
    };

    // 3. Surface Rapid Transit (max 1.5)
    const scoreSRT = (dist) => {
        if (dist == null) return 0;
        if (dist <= 0.8) return 1.5;
        if (dist <= 1.5) return 1.0;
        if (dist <= 3) return 0.5;
        return 0;
    };

    // 4. Bus Stop (max 1.0)
    const scoreBus = (dist) => {
        if (dist == null) return 0; // no bus stop within 10 km
        if (dist <= 0.4) return 1.0;
        if (dist <= 0.8) return 0.7;
        if (dist <= 1.5) return 0.3;
        return 0;
    };

    // 5. Facilities (max 1.5)
    const scoreFacility = (dist, maxDist, weight) => {
        if (!dist || dist > maxDist) return 0;
        return weight * (1 - dist / maxDist);
    };
    const calculateFacilitiesScore = () => {
        let score = 0;

        const groceries = nearbyStores?.grocery
            ? Math.min(...Object.values(nearbyStores.grocery).map(s => s.distance_km))
            : null;

        const banks = nearbyStores?.banks
            ? Math.min(...Object.values(nearbyStores.banks).map(s => s.distance_km))
            : null;

        const pharmacy = nearbyStores?.pharmacy
            ? Math.min(...Object.values(nearbyStores.pharmacy).map(s => s.distance_km))
            : null;

        const childcare = childrenService?.distance_km ?? null;

        score += scoreFacility(groceries, 5, 0.6);
        score += scoreFacility(pharmacy, 5, 0.4);
        score += scoreFacility(banks, 5, 0.3);
        score += scoreFacility(childcare, 5, 0.2);

        return score;
    };
    useEffect(() => {
        // wait until all four score inputs have finished loading; one that found nothing
        // (e.g. no bus stop within 10 km) counts as 0 points, a failed one makes the score "Unavailable"
        if ([nearestCorridor, nearestGreenland, busStop, nearestSRT].some((input) => input === undefined)) {
            return;
        }
        if (scoreInputsFailed) {
            return;
        }

        let total = 0;

        total += scoreCorridor(nearestCorridor?.distance_km);
        total += scoreGreenland(nearestGreenland?.inside_polygon);
        total += scoreSRT(nearestSRT?.distance_km);
        total += scoreBus(busStop?.distance_km);
        total += calculateFacilitiesScore();

        // ❌ Penalty اگر داخل Greenlands باشد
        if (nearestGreenland?.inside_polygon) {
            total *= 0.3;
        }

        // clamp بین 0 تا 10
        total = Math.max(0, Math.min(10, total));

        setProjectScore(total);

    }, [
        nearestCorridor,
        nearestGreenland,
        nearestSRT,
        busStop,
        nearbyStores,
        childrenService,
        scoreInputsFailed,
    ]);

    const scoreText =
        projectScore !== null ? `${projectScore.toFixed(2)} / 10`
            : scoreInputsFailed ? "Unavailable"
                : "Calculating...";




    // ===== helpers =====
    function skeletonLine(width, height = 12) {
        return {
            width,
            height,
            borderRadius: "999px",
            background:
                "linear-gradient(90deg, rgba(0,0,0,0.05), rgba(0,0,0,0.09), rgba(0,0,0,0.05))",
            backgroundSize: "200% 100%",
            animation: "pulse 1.2s ease-in-out infinite",
        };
    }

    const spinnerWrap = {
        width: "44px",
        height: "44px",
        borderRadius: "14px",
        background: "rgba(244,158,26,0.14)",
        border: "1px solid rgba(244,158,26,0.35)",
        display: "grid",
        placeItems: "center",
    };

    const spinner = {
        width: "22px",
        height: "22px",
        borderRadius: "50%",
        border: "3px solid rgba(244,158,26,0.25)",
        borderTop: "3px solid rgba(244,158,26,0.95)",
        animation: "spin 0.9s linear infinite",
    };

    if (projectNotFound) {
        return (
            <div
                style={{
                    minHeight: "70vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px 16px",
                    background: "linear-gradient(135deg, #fff7e8, #ffffff)",
                }}
            >
                <div style={{ width: "min(980px, 100%)" }}>
                    <EmptyPanel title={projectNotFound.title} text={projectNotFound.text} />
                </div>
            </div>
        );
    }

    // ✅ Professional Loading UI (Skeleton + Spinner)
    if (!project) {
        return (
            <div
                style={{
                    minHeight: "70vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "40px 16px",
                    background: "linear-gradient(135deg, #fff7e8, #ffffff)",
                }}
            >
                <div
                    style={{
                        width: "min(980px, 100%)",
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "14px",
                    }}
                >
                    {/* Top header skeleton */}
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "16px",
                            padding: "18px",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                            border: "1px solid rgba(0,0,0,0.06)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <div style={skeletonLine(260, 16)} />
                            <div style={{ height: "10px" }} />
                            <div style={skeletonLine(420, 12)} />
                        </div>

                        {/* Spinner */}
                        <div style={spinnerWrap}>
                            <div style={spinner} />
                        </div>
                    </div>

                    {/* Body: map + info skeleton */}
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1.35fr 1fr",
                            gap: "14px",
                        }}
                    >
                        {/* Map skeleton */}
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: "16px",
                                overflow: "hidden",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                                border: "1px solid rgba(0,0,0,0.06)",
                                minHeight: "420px",
                                position: "relative",
                            }}
                        >
                            <div style={{ padding: "16px" }}>
                                <div style={skeletonLine(180, 12)} />
                            </div>

                            <div
                                style={{
                                    height: "100%",
                                    background:
                                        "linear-gradient(90deg, rgba(0,0,0,0.03), rgba(0,0,0,0.06), rgba(0,0,0,0.03))",
                                    backgroundSize: "200% 100%",
                                    animation: "pulse 1.4s ease-in-out infinite",
                                }}
                            />

                            <div
                                style={{
                                    position: "absolute",
                                    bottom: "16px",
                                    left: "16px",
                                    right: "16px",
                                    background: "rgba(255,255,255,0.82)",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    borderRadius: "12px",
                                    padding: "12px",
                                    backdropFilter: "blur(10px)",
                                }}
                            >
                                <div style={skeletonLine("70%", 12)} />
                                <div style={{ height: "8px" }} />
                                <div style={skeletonLine("45%", 12)} />
                            </div>
                        </div>

                        {/* Side panel skeleton */}
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: "16px",
                                padding: "16px",
                                boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                                border: "1px solid rgba(0,0,0,0.06)",
                                minHeight: "420px",
                                display: "flex",
                                flexDirection: "column",
                                gap: "12px",
                            }}
                        >
                            <div style={skeletonLine("55%", 14)} />
                            <div style={skeletonLine("85%", 12)} />
                            <div style={skeletonLine("75%", 12)} />
                            <div style={{ height: "10px" }} />

                            {/* Skeleton cards */}
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    style={{
                                        borderRadius: "14px",
                                        border: "1px solid rgba(0,0,0,0.06)",
                                        padding: "12px",
                                        background: "rgba(0,0,0,0.02)",
                                    }}
                                >
                                    <div style={skeletonLine("60%", 12)} />
                                    <div style={{ height: "8px" }} />
                                    <div style={skeletonLine("90%", 10)} />
                                </div>
                            ))}

                            <div style={{ marginTop: "auto" }}>
                                <div
                                    style={{
                                        fontSize: "12px",
                                        color: "#666",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <span style={{ fontWeight: 600, color: "#333" }}>Loading</span>
                                    <span>project details, zoning & layers…</span>
                                </div>
                                <div style={{ height: "8px" }} />
                                <div
                                    style={{
                                        height: "8px",
                                        borderRadius: "999px",
                                        background: "rgba(244,158,26,0.18)",
                                        overflow: "hidden",
                                    }}
                                >
                                    <div
                                        style={{
                                            height: "100%",
                                            width: "55%",
                                            borderRadius: "999px",
                                            background: "rgba(244,158,26,0.85)",
                                            animation: "progress 1.2s ease-in-out infinite alternate",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CSS keyframes */}
                    <style>
                        {`
            @keyframes pulse {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            @keyframes progress {
              from { width: 35%; }
              to { width: 85%; }
            }
          `}
                    </style>
                </div>
            </div>
        );
    }


    const greenlandDistanceM = nearestGreenland?.distance_km != null
        ? Number(nearestGreenland.distance_km) * 1000
        : null;

    const envConstraintLevel = environmentalReport?.constraint_level || null;
    const envHardRejectCount = environmentalReport?.hard_reject_triggers?.length || 0;
    const envHighCount = environmentalReport?.high_constraint_triggers?.length || 0;
    const envModerateCount = environmentalReport?.moderate_constraint_triggers?.length || 0;
    const envSeverityScore = envHardRejectCount * 10 + envHighCount * 5 + envModerateCount * 2;

    const envMessage = (() => {
        if (!environmentalReport) {
            return loadingEnvironmental
                ? "Loading environmental constraint data..."
                : "Environmental constraint data is unavailable right now (the request may have failed).";
        }
        if (envHardRejectCount > 0) {
            return `Parcel failed hard environmental constraint screening: ${environmentalReport.hard_reject_triggers.join(", ")}.`;
        }
        if (envHighCount > 0) {
            return `Parcel has high environmental constraint triggers: ${environmentalReport.high_constraint_triggers.join(", ")}.`;
        }
        if (envModerateCount > 0) {
            return `Parcel has moderate environmental constraint triggers: ${environmentalReport.moderate_constraint_triggers.join(", ")}.`;
        }
        if (environmentalReport.environmental_impact_study_required) {
            return "No major constraint, but the parcel is within 120m of a key natural heritage or hydrologic feature and may require an Environmental Impact Study.";
        }
        return "No environmental constraint detected from the currently loaded Chapter 3 layers.";
    })();

    // "Show on map" checkboxes for the 5-Layer Parcel Report. Infrastructure items reuse the
    // Layer Controls toggles, so both checkboxes stay in sync and draw the same overlay.
    const sharedMapToggles = {
        infra_bus: [showBusStopOnMap, setShowBusStopOnMap, !!busStop],
        infra_srt: [showSRTOnMap, setShowSRTOnMap, !!nearestSRT],
        infra_groceries: [showGroceriesOnMap, setShowGroceriesOnMap, !!nearbyStores?.grocery],
        infra_banks: [showBanksOnMap, setShowBanksOnMap, !!nearbyStores?.banks],
        infra_pharmacy: [showPharmacyOnMap, setShowPharmacyOnMap, !!nearbyStores?.pharmacy],
        infra_children: [showChildrenServiceOnMap, setShowChildrenServiceOnMap, !!childrenService],
    };

    const renderMapToggle = (key, label) => {
        if (sharedMapToggles[key]) {
            const [checked, setChecked, available] = sharedMapToggles[key];
            return available ? <ShowOnMapToggle checked={checked} onChange={setChecked} label={label} /> : null;
        }
        // only offered when the backend sent something to draw (closest_point + map_geometry)
        if (!reportMapTargets[key]?.closest_point) return null;
        return (
            <ShowOnMapToggle
                checked={reportMapLayers[key]}
                onChange={(on) => setReportMapLayers((prev) => ({ ...prev, [key]: on }))}
                label={label}
            />
        );
    };

    const parcelReport = project ? {
        score: projectScore,
        score_text: scoreText,
        constraint_summary: {
            status: envConstraintLevel || "UNKNOWN",
            hard_reject: envHardRejectCount > 0,
            total_penalty: envSeverityScore,
            message: envMessage,
        },
        environmental_constraints: environmentalReport,
        growth_opportunity: {
            project_score: projectScore,
            nearest_corridor: nearestCorridor,
        },
        land_use_urban_structure: {
            land_use: project?.LANDUSE_DESIG,
            municipality: project?.MUN_NAME,
            application_type: project?.APPL_TYPE,
            ...landUseReport,
        },
        infrastructure_feasibility: {
            nearest_bus_stop: busStop,
            no_bus_stop_within_10km: busStop === null && !busError,
            nearest_srt: nearestSRT,
            children_service: childrenService,
            nearby_stores: nearbyStores,
        },
        policy_approval_logic: {
            application_stage: project?.APPL_STAGE,
            application_step: project?.APPL_STEP,
            application_status: project?.APPL_STATUS,
        },
    } : null;


    return (
        <div style={reportStyles.page}>
            <div style={reportStyles.topGrid}>
                <section style={reportStyles.projectPanel}>
                    <div style={reportStyles.pageEyebrow}>{isParcelSource ? "Parcel Detail" : "Project Detail"}</div>
                    <h1 style={reportStyles.pageTitle}>Detail of {project.APPL_NAME}</h1>

                    <SummaryCard title={isParcelSource ? "Parcel Information" : "Project Information"}>
                        {isParcelSource ? (
                            <>
                                <InfoRow label="Address" value={project.ADDRESS || "N/A"} />
                                <InfoRow label="Parcel ID" value={project.PAR_GIS_ID} />
                                <InfoRow label="Plan Number" value={project.PLANNUM || "N/A"} />
                                <InfoRow label="Parcel Type" value={project.PAR_TYPE || "N/A"} />
                                <InfoRow label="Status" value={project.LIFESTATUS || "N/A"} />
                                <InfoRow label="Municipality" value={project.MUN_NAME || "N/A"} />
                            </>
                        ) : (
                            <>
                                <InfoRow label="Address" value={project.ADDRESS} />
                                <InfoRow label="Application Type" value={project.APPL_TYPE || "N/A"} />
                                <InfoRow label="Application Stage" value={project.APPL_STAGE || "N/A"} />
                                <InfoRow label="Application Step" value={project.APPL_STEP || "N/A"} />
                                <InfoRow label="Status" value={project.APPL_STATUS || "N/A"} />
                                <InfoRow label="Land Use" value={project.LANDUSE_DESIG || "N/A"} />
                                <InfoRow label="Municipality" value={project.MUN_NAME || "N/A"} />
                            </>
                        )}
                        <InfoRow
                            label="Area"
                            value={project.Shape__Area ? `${Math.round(project.Shape__Area).toLocaleString()} m² / ${Math.round(project.Shape__Area * 10.7639).toLocaleString()} ft²` : "N/A"}
                        />
                    </SummaryCard>

                    <SummaryCard title="Overall Result">
                        <InfoRow label="Score" value={scoreText} />
                        <InfoRow label="Constraint Status" value={parcelReport?.constraint_summary?.status} />
                        <InfoRow label="Hard Reject" value={parcelReport?.constraint_summary?.hard_reject ? "Yes" : "No"} />
                        <InfoRow label="Penalty" value={parcelReport?.constraint_summary?.total_penalty} />
                    </SummaryCard>
                </section>

                <section style={reportStyles.mapAndSummaryPanel}>
                    <div style={reportStyles.mapCard}>
                        <div id="map" style={reportStyles.googleMap} />
                    </div>

                    <aside style={reportStyles.summaryPanel}>
                        <div style={reportStyles.summaryPanelHeader}>
                            <h2 style={reportStyles.summaryPanelTitle}>Parcel Summary</h2>
                            <div style={reportStyles.summaryPanelSub}>Summary information scrolls within this panel.</div>
                        </div>

                        <div style={reportStyles.summaryScrollArea}>
                            <SummaryCard title="Guide">
                                <div style={reportStyles.guideText}>
                                    The selected project parcel is displayed on the map. Use the layer controls below to show nearby constraints and services.
                                </div>
                            </SummaryCard>

                            <SummaryCard title="Selected Parcel">
                                <InfoRow label="Name" value={project.APPL_NAME} />
                                <InfoRow label="Address" value={project.ADDRESS} />
                                <InfoRow label="Municipality" value={project.MUN_NAME} />
                            </SummaryCard>

                            <SummaryCard title="Environmental Result">
                                <InfoRow label="Greenlands Status" value={nearestGreenland?.inside_polygon ? "Inside Greenlands" : "Outside Greenlands"} />
                                <InfoRow label="Distance to Greenlands" value={greenlandDistanceM != null ? `${Math.round(greenlandDistanceM)} m` : "-"} />
                                <InfoRow label="Within 120m" value={greenlandDistanceM != null && greenlandDistanceM <= 120 ? "Yes" : "No"} />
                            </SummaryCard>
                        </div>
                    </aside>
                </section>
            </div>

            {/* Neighbourhood Accessibility Report / Layer Controls: hidden for now (kept, not removed).
                Its map toggles' state and effects are still used by the 5-Layer Parcel Report.
            <section style={reportStyles.accessibilitySection}>
                <div style={reportStyles.reportHeader}>
                    <div>
                        <div style={reportStyles.pageEyebrow}>Neighbourhood Accessibility Report</div>
                        <h2 style={reportStyles.reportMainTitle}>Layer Controls</h2>
                    </div>
                    <StatusPill text={projectScore !== null ? `${projectScore.toFixed(2)} / 10` : "Calculating"} tone="neutral" />
                </div>

                <div style={reportStyles.tabsBar}>
                    <button onClick={() => setActiveTab("official")} style={{ ...reportStyles.tabButton, ...(activeTab === "official" ? reportStyles.tabButtonActive : {}) }}>Official Plan</button>
                    <button onClick={() => setActiveTab("infra")} style={{ ...reportStyles.tabButton, ...(activeTab === "infra" ? reportStyles.tabButtonActive : {}) }}>Infrastructures</button>
                    <button onClick={() => setActiveTab("facilities")} style={{ ...reportStyles.tabButton, ...(activeTab === "facilities" ? reportStyles.tabButtonActive : {}) }}>Facilities</button>
                </div>

                <div style={reportStyles.controlBody}>
                    {activeTab === "official" && (
                        <div style={reportStyles.controlGrid}>
                            <SummaryCard title="Regional Corridor / Center">
                                {loadingCorridor && "Loading corridor..."}
                                {nearestCorridor && <>
                                    <InfoRow label="Distance" value={`${nearestCorridor.distance_km} km`} />
                                    <label><input type="checkbox" checked={showCorridorOnMap} onChange={(e) => setShowCorridorOnMap(e.target.checked)} /> Show on map</label>
                                </>}
                            </SummaryCard>

                            <SummaryCard title="Regional Greenlands System">
                                {loadingGreenland && "Loading greenland..."}
                                {nearestGreenland && <>
                                    <InfoRow label="Status" value={nearestGreenland.inside_polygon ? "Project is inside Greenlands" : "Outside Greenlands"} />
                                    <InfoRow label="Distance" value={nearestGreenland.distance_km != null ? `${nearestGreenland.distance_km} km` : "-"} />
                                    <label><input type="checkbox" checked={showGreenlandOnMap} onChange={(e) => setShowGreenlandOnMap(e.target.checked)} /> Show on map</label>
                                </>}
                            </SummaryCard>
                        </div>
                    )}

                    {activeTab === "infra" && (
                        <div style={reportStyles.controlGrid}>
                            <SummaryCard title="Nearest Bus Stop">
                                {loadingBus && "Loading bus stop..."}
                                {busError && <span style={{ color: "red" }}>{busError}</span>}
                                {busStop && <>
                                    <InfoRow label="Distance" value={`${busStop.distance_km} km`} />
                                    <InfoRow label="Name" value={busStop.name} />
                                    <label><input type="checkbox" checked={showBusStopOnMap} onChange={(e) => setShowBusStopOnMap(e.target.checked)} /> Show bus stop on map</label>
                                </>}
                            </SummaryCard>

                            <SummaryCard title="Surface Rapid Transit Service">
                                {nearestSRT ? <>
                                    <InfoRow label="Distance" value={`${nearestSRT.distance_km} km`} />
                                    <label><input type="checkbox" checked={showSRTOnMap} onChange={(e) => setShowSRTOnMap(e.target.checked)} /> Show SRT on map</label>
                                </> : "Loading..."}
                            </SummaryCard>
                        </div>
                    )}

                    {activeTab === "facilities" && (
                        <div style={reportStyles.controlGrid}>
                            <SummaryCard title="Nearest Groceries">
                                {loadingStores && <div>Loading groceries...</div>}
                                {nearbyStores?.grocery && <>
                                    {sortByDistance(nearbyStores.grocery).map(([brand, store]) => <InfoRow key={brand} label={brand} value={`${store.distance_km} km`} />)}
                                    <label><input type="checkbox" checked={showGroceriesOnMap} onChange={(e) => setShowGroceriesOnMap(e.target.checked)} /> Show on map</label>
                                </>}
                            </SummaryCard>

                            <SummaryCard title="Nearest Banks">
                                {nearbyStores?.banks && <>
                                    {sortByDistance(nearbyStores.banks).map(([brand, bank]) => <InfoRow key={brand} label={brand} value={`${bank.distance_km} km`} />)}
                                    <label><input type="checkbox" checked={showBanksOnMap} onChange={(e) => setShowBanksOnMap(e.target.checked)} /> Show on map</label>
                                </>}
                            </SummaryCard>

                            <SummaryCard title="Nearest Pharmacy">
                                {nearbyStores?.pharmacy && <>
                                    {sortByDistance(nearbyStores.pharmacy).map(([brand, p]) => <InfoRow key={brand} label={brand} value={`${p.distance_km} km`} />)}
                                    <label><input type="checkbox" checked={showPharmacyOnMap} onChange={(e) => setShowPharmacyOnMap(e.target.checked)} /> Show on map</label>
                                </>}
                            </SummaryCard>

                            <SummaryCard title="Children Services">
                                {!childrenService && <div>Loading...</div>}
                                {childrenService && <>
                                    <InfoRow label="Name" value={childrenService.feature?.properties?.NAME} />
                                    <InfoRow label="Category" value={childrenService.feature?.properties?.CATEGORY} />
                                    <InfoRow label="Distance" value={`${childrenService.distance_km} km`} />
                                    <label><input type="checkbox" checked={showChildrenServiceOnMap} onChange={(e) => setShowChildrenServiceOnMap(e.target.checked)} /> Show on map</label>
                                </>}
                            </SummaryCard>
                        </div>
                    )}
                </div>
            </section>
            */}

            <section style={reportStyles.bottomReportSection}>
                <div style={reportStyles.reportHeader}>
                    <div>
                        <div style={reportStyles.pageEyebrow}>Parcel Analysis</div>
                        <h2 style={reportStyles.reportMainTitle}>5-Layer Parcel Report</h2>
                    </div>
                </div>

                <div style={reportStyles.tabsBar}>
                    {REPORT_TABS.map((tab) => {
                        const isActive = reportActiveTab === tab.key;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setReportActiveTab(tab.key)}
                                style={{ ...reportStyles.tabButton, ...(isActive ? reportStyles.tabButtonActive : {}) }}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div style={reportStyles.reportBody}>
                    <ReportTabContent activeTab={reportActiveTab} parcelReport={parcelReport} loadingLandUse={loadingLandUse} loadingEnvironmental={loadingEnvironmental} renderMapToggle={renderMapToggle} />
                </div>
            </section>
        </div>
    );
};


const reportStyles = {
    page: {
        width: "100%",
        minHeight: "100vh",
        background: "#f8fafc",
        padding: 18,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 18,
    },
    topGrid: {
        display: "grid",
        gridTemplateColumns: "360px minmax(0, 1fr)",
        gap: 18,
        alignItems: "stretch",
    },
    projectPanel: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 14,
    },
    mapAndSummaryPanel: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 380px",
        gap: 18,
        minHeight: 520,
    },
    mapCard: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        overflow: "hidden",
        minHeight: 520,
    },
    googleMap: {
        width: "100%",
        height: "100%",
        minHeight: 520,
    },
    pageEyebrow: {
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#f97316",
        marginBottom: 6,
    },
    pageTitle: {
        margin: 0,
        fontSize: 28,
        lineHeight: 1.15,
        color: "#0f172a",
    },
    summaryPanel: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 520,
    },
    summaryPanelHeader: {
        padding: 20,
        borderBottom: "1px solid #eef2f7",
    },
    summaryPanelTitle: {
        margin: 0,
        fontSize: 22,
        lineHeight: 1.1,
        color: "#0f172a",
    },
    summaryPanelSub: {
        marginTop: 6,
        color: "#64748b",
        fontSize: 13,
    },
    summaryScrollArea: {
        padding: 16,
        overflowY: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 14,
    },
    summaryCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 18,
        padding: 16,
        background: "#f8fafc",
    },
    summaryCardTitle: {
        margin: "0 0 12px 0",
        fontSize: 18,
        color: "#0f172a",
    },
    guideText: {
        color: "#475569",
        lineHeight: 1.8,
        fontSize: 14,
    },
    accessibilitySection: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        overflow: "hidden",
    },
    bottomReportSection: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 24,
        boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: "34vh",
    },
    reportHeader: {
        padding: "18px 20px 10px 20px",
        borderBottom: "1px solid #eef2f7",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
    },
    reportMainTitle: {
        margin: 0,
        fontSize: 30,
        lineHeight: 1.1,
        color: "#0f172a",
    },
    tabsBar: {
        display: "flex",
        gap: 10,
        padding: "14px 18px",
        flexWrap: "wrap",
        borderBottom: "1px solid #eef2f7",
        background: "#fcfcfd",
    },
    tabButton: {
        border: "1px solid #e5e7eb",
        background: "#ffffff",
        borderRadius: 999,
        padding: "10px 14px",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 700,
        color: "#334155",
        transition: "all 0.2s ease",
    },
    tabButtonActive: {
        background: "#fff7ed",
        color: "#c2410c",
        border: "1px solid #fdba74",
        boxShadow: "0 6px 14px rgba(249,115,22,0.12)",
    },
    controlBody: { padding: 18 },
    controlGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 14,
    },
    reportBody: { padding: 18 },
    reportTabContent: {
        display: "flex",
        flexDirection: "column",
        gap: 18,
    },
    reportHeroCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 20,
        background: "#f8fafc",
        padding: 18,
    },
    reportHeroHeader: {
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        alignItems: "flex-start",
        marginBottom: 16,
    },
    reportHeroEyebrow: {
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#f97316",
        marginBottom: 6,
    },
    reportHeroTitle: {
        margin: 0,
        fontSize: 26,
        lineHeight: 1.15,
        color: "#0f172a",
    },
    metricGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14,
        marginBottom: 16,
    },
    metricCard: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 14,
    },
    metricLabel: { color: "#64748b", fontSize: 12, fontWeight: 700, marginBottom: 6 },
    metricValue: { color: "#0f172a", fontSize: 20, fontWeight: 800 },
    messageBox: {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: 14,
        color: "#334155",
        lineHeight: 1.7,
    },
    messageBoxSoft: {
        background: "#ffffff",
        border: "1px dashed #cbd5e1",
        borderRadius: 14,
        padding: 14,
        color: "#475569",
        lineHeight: 1.7,
    },
    layerGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 16,
    },
    constraintLayerCard: {
        borderRadius: 20,
        padding: 16,
    },
    constraintLayerTitle: {
        margin: "0 0 12px 0",
        color: "#0f172a",
        fontSize: 18,
    },
    layerTopStatus: { marginBottom: 12 },
    infoGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: 10,
    },
    infoChip: {
        background: "rgba(255,255,255,0.85)",
        border: "1px solid rgba(203,213,225,0.8)",
        borderRadius: 14,
        padding: 12,
    },
    infoChipLabel: { color: "#64748b", fontSize: 12, fontWeight: 700, marginBottom: 4 },
    infoChipValue: { color: "#0f172a", fontSize: 15, fontWeight: 800 },
    layerDescription: {
        marginTop: 12,
        color: "#475569",
        lineHeight: 1.7,
        fontSize: 14,
    },
    showOnMapToggle: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 12,
        color: "#334155",
        fontSize: 14,
        fontWeight: 600,
        cursor: "pointer",
    },
    reportSection: {
        border: "1px solid #e5e7eb",
        borderRadius: 18,
        padding: 16,
        background: "#ffffff",
    },
    reportSectionTitle: {
        margin: "0 0 12px 0",
        color: "#0f172a",
        fontSize: 18,
    },
    preBlock: {
        margin: 0,
        background: "#0f172a",
        color: "#e2e8f0",
        borderRadius: 14,
        padding: 14,
        overflowX: "auto",
        fontSize: 12,
    },
    emptyPanel: {
        border: "1px dashed #cbd5e1",
        borderRadius: 18,
        padding: 22,
        background: "#f8fafc",
    },
    emptyPanelTitle: {
        color: "#0f172a",
        fontSize: 18,
        fontWeight: 800,
        marginBottom: 8,
    },
    emptyPanelText: { color: "#475569", lineHeight: 1.7 },
    statusPill: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 999,
        padding: "7px 10px",
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: "nowrap",
    },
    infoRow: {
        display: "grid",
        gridTemplateColumns: "140px minmax(0, 1fr)",
        gap: 10,
        padding: "8px 0",
        borderBottom: "1px solid rgba(226,232,240,0.9)",
    },
    infoLabel: { color: "#64748b", fontSize: 13, fontWeight: 700 },
    infoValue: { color: "#0f172a", fontSize: 13, fontWeight: 700, overflowWrap: "anywhere" },
};

export default ProjectDetail;
